import { useEffect, useState } from 'react';

type ThemePreference = 'dark' | 'light' | 'system';

function readResolvedDarkMode(themePreference: ThemePreference): boolean {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    if (root.classList.contains('dark')) return true;
    if (root.classList.contains('light')) return false;
  }

  if (themePreference === 'dark') return true;
  if (themePreference === 'light') return false;

  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function useResolvedDarkMode(themePreference: ThemePreference): boolean {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => readResolvedDarkMode(themePreference));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setIsDarkMode(readResolvedDarkMode(themePreference));

    update();

    const observer = new MutationObserver(() => {
      update();
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
    });

    mediaQuery.addEventListener('change', update);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', update);
    };
  }, [themePreference]);

  return isDarkMode;
}
